import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero, Reveal, SectionHead, PhotoFrame, EmptyState, Tag } from "@/components/Shared";
import { useCollection, fmtDate, IMG } from "@/lib/api";

const CATEGORIES = ["All", "Member Stories", "Opinion", "Rotaract Stories", "Social Issues", "Experiences", "Leadership", "Campus Life"];

export default function Blog() {
  const { items, loading } = useCollection("blog");
  const [filter, setFilter] = useState("All");
  const posts = (items || []).filter((p) => filter === "All" || p.category === filter);
  const [featured, ...rest] = posts;

  return (
    <main>
      <PageHero eyebrow="The RCSCW Blog" title="Stories in" italic="our own voice" image={IMG.celestialSky}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          Member stories, opinions and experiences — the editorial voice of the club, written by the people living it.
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="blog-section">
        <Reveal>
          <div className="flex flex-wrap gap-3" data-testid="blog-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300 ${
                  filter === c ? "border-ember bg-ember/10 text-ember" : "border-silver/20 text-silver-muted hover:border-silver/50 hover:text-silver"
                }`}
                data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {loading && <p className="mt-12 text-sm text-silver-muted">Loading stories…</p>}

        {featured && (
          <Reveal delay={0.1}>
            <Link to={`/blog/${featured.id}`} className="card-line group mt-12 grid gap-0 overflow-hidden lg:grid-cols-2" data-testid="blog-featured">
              <PhotoFrame src={featured.cover_image} label={featured.title} className="aspect-video lg:aspect-auto lg:h-full lg:min-h-[320px]" imgClassName="transition-transform duration-700 group-hover:scale-105" />
              <div className="flex flex-col justify-center p-8 sm:p-12">
                <div className="flex items-center gap-3">
                  <Tag tone="ember">{featured.category}</Tag>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">Featured</span>
                </div>
                <h2 className="mt-6 font-display text-3xl font-semibold leading-tight text-silver sm:text-4xl">{featured.title}</h2>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-silver-muted">{featured.excerpt}</p>
                <div className="mt-8 flex items-center justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
                    {featured.author} · {fmtDate(featured.date)}
                  </p>
                  <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-silver-dim transition-all duration-300 group-hover:gap-3.5 group-hover:text-white">
                    Read More <ArrowUpRight size={14} className="text-ember" />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08} className="h-full">
              <Link to={`/blog/${p.id}`} className="card-line group flex h-full flex-col" data-testid={`blog-card-${p.id}`}>
                <PhotoFrame src={p.cover_image} label={p.title} className="aspect-video" imgClassName="transition-transform duration-700 group-hover:scale-105" />
                <div className="flex flex-1 flex-col p-6">
                  <Tag>{p.category}</Tag>
                  <h3 className="mt-4 font-display text-2xl font-semibold text-silver">{p.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-silver-muted">{p.excerpt}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
                      {p.author} · {fmtDate(p.date)}
                    </p>
                    <ArrowUpRight size={15} className="text-ember transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {!loading && posts.length === 0 && (
          <div className="mt-12">
            <EmptyState title="No stories in this category yet" note="Members can publish through the Admin panel — the blog grows all year." />
          </div>
        )}
      </section>
    </main>
  );
}
